import { useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useScrollScene, type ScrollScene } from './useScrollScene';

/**
 * Layout shared by the homepage scroll-driven 3D sections (2026-10-10):
 * a tall section whose sticky panel holds the canvas, a still poster until
 * the scene is ready, the stage captions (right on desktop — RTL start —,
 * bottom card on phones), a progress rail and the model credit. With no 3D
 * (no WebGL 2 / reduced motion) it renders `staticBody` instead.
 */
export interface SceneStage {
  at: number;
  /** Part the arrow and name tag point at (scene-specific id). */
  focus?: string;
  /** Parts outlined while this caption shows. */
  parts?: string[];
  /** Short name on the tag next to the part. */
  tag?: string;
  /** Real-looking photo of the part: /generated-images/parts/<photo>-{360,720}.webp */
  photo?: string;
  photoAlt?: string;
  title: string;
  text: string;
  link?: { to: string; label: string };
}

/** What a scene needs to support the "this part" overlay. */
export interface FocusableScene extends ScrollScene {
  project?(partId: string): { x: number; y: number } | null;
  setFocus?(partIds: string[]): void;
  /** Phones: the free band between the header and the caption card, as
   *  fractions of the canvas height (null = desktop layout). */
  setSafeArea?(area: { top: number; bottom: number } | null): void;
}

export default function ScrollSceneSection<T extends FocusableScene>(props: {
  id: string;
  stages: SceneStage[];
  poster: string;
  /** Tailwind height classes for the scroll length, e.g. "h-[340vh] md:h-[420vh]". */
  heightClass: string;
  create: (host: HTMLDivElement, onFrame: () => void) => Promise<T>;
  eager?: boolean;
  finalCta?: ReactNode;
  credit: ReactNode;
  staticBody: ReactNode;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const tagRef = useRef<HTMLDivElement>(null);
  const leaderRef = useRef<SVGPathElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const focusedStage = useRef(-1);
  const { stages } = props;

  // Every frame: outline the stage's parts (on change), and draw the tag and
  // the arrow from the part to the caption — the user always sees which part
  // the caption is about (owner's request, 2026-10-10).
  const placeOverlay = (scene: T, i: number) => {
    const st = stages[i];
    // Phones (owner, 2026-10-14: "the caption hides part of the PC"): tell the
    // scene where the card starts, every frame — its height changes per stage.
    if (scene.setSafeArea && stickyRef.current && panelRef.current) {
      const b = stickyRef.current.getBoundingClientRect();
      const c = panelRef.current.getBoundingClientRect();
      scene.setSafeArea(b.width < 768 ? { top: 76 / b.height, bottom: Math.max(0.35, (c.top - b.top - 14) / b.height) } : null);
    }
    if (focusedStage.current !== i) {
      focusedStage.current = i;
      scene.setFocus?.(st.parts ?? []);
    }
    const tag = tagRef.current;
    const leader = leaderRef.current;
    const pos = st.focus && scene.project ? scene.project(st.focus) : null;
    if (!tag || !leader) return;
    if (!pos) {
      tag.style.opacity = '0';
      leader.style.opacity = '0';
      return;
    }
    tag.style.opacity = '1';
    tag.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
    const box = stickyRef.current!.getBoundingClientRect();
    const card = panelRef.current!.getBoundingClientRect();
    const desktop = box.width >= 768;
    const ax = desktop ? card.left - box.left - 14 : card.left - box.left + card.width / 2;
    const ay = desktop ? card.top - box.top + 56 : card.top - box.top - 8;
    const d = desktop
      ? `M${ax},${ay} C${(ax + pos.x) / 2},${ay} ${(ax + pos.x) / 2},${pos.y} ${pos.x},${pos.y}`
      : `M${ax},${ay} C${ax},${(ay + pos.y) / 2} ${pos.x},${(ay + pos.y) / 2} ${pos.x},${pos.y}`;
    leader.setAttribute('d', d);
    leader.style.opacity = '1';
  };

  const { stage, mode, ready } = useScrollScene<T>({
    sectionRef,
    hostRef,
    create: props.create,
    stageStarts: stages.map((s) => s.at),
    eager: props.eager,
    onFrame: placeOverlay,
  });
  const current = stages[stage];

  // Warm the part photos once the scene is up, so they appear instantly.
  useEffect(() => {
    if (!ready) return;
    stages.forEach((s) => {
      if (s.photo) new Image().src = `/generated-images/parts/${s.photo}-360.webp`;
    });
  }, [ready, stages]);
  const titleId = `${props.id}-title`;

  if (mode === 'static') {
    return (
      <section ref={sectionRef} className="cin-dark font-heebo relative isolate overflow-hidden bg-[#0B1224] text-white py-[clamp(64px,10vw,120px)]">
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-[radial-gradient(800px_480px_at_80%_10%,rgba(30,85,217,0.4),transparent_62%),radial-gradient(640px_420px_at_10%_90%,rgba(124,77,255,0.28),transparent_60%)]" />
        <div className="mx-auto max-w-[1280px] px-5 md:px-8 lg:px-20">
          {props.staticBody}
          <div className="mt-10">{props.credit}</div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} aria-labelledby={titleId} className={`cin-dark font-heebo relative isolate bg-[#0B1224] text-white ${props.heightClass}`}>
      <div ref={stickyRef} className="sticky top-0 h-[100svh] overflow-hidden">
        {/* Still image until the 3D scene is ready. */}
        <img
          src={props.poster}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          className={`absolute inset-x-0 top-[8%] mx-auto h-[52%] w-[92%] md:inset-y-0 md:my-auto md:right-[420px] md:h-[78%] md:w-auto md:max-w-[60%] object-contain transition-opacity duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] ${ready ? 'opacity-0' : 'opacity-100'}`}
        />
        <div ref={hostRef} aria-hidden="true" data-prerender-skip className="absolute inset-0" />
        {/* Section glows, above the (opaque) canvas so they also tint the scene. */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 mix-blend-screen bg-[radial-gradient(900px_560px_at_75%_20%,rgba(30,85,217,0.2),transparent_62%),radial-gradient(700px_460px_at_15%_85%,rgba(124,77,255,0.14),transparent_60%)]" />

        {/* Arrow from the caption to the part it talks about. */}
        <svg aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full overflow-visible">
          <defs>
            <marker id={`${props.id}-arrow`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" fill="#5bd3ff" />
            </marker>
          </defs>
          <path ref={leaderRef} fill="none" stroke="#5bd3ff" strokeWidth="1.6" strokeDasharray="5 4" markerEnd={`url(#${props.id}-arrow)`} className="opacity-0 transition-opacity duration-300" />
        </svg>
        {/* Name tag on the part */}
        <div ref={tagRef} aria-hidden="true" className="pointer-events-none absolute left-0 top-0 opacity-0 transition-opacity duration-200">
          <span className="absolute -left-[7px] -top-[7px] h-3.5 w-3.5 rounded-full bg-[#5bd3ff] shadow-[0_0_0_6px_rgba(91,211,255,0.25)] motion-safe:animate-pulse" />
          {current.tag && (
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#0B1224]/85 px-3 py-1 text-[13px] font-bold text-white ring-1 ring-[#5bd3ff]/60 backdrop-blur">
              {current.tag}
            </span>
          )}
        </div>

        <a href={`#after-${props.id}`} className="skip-to-content">דלגו על האנימציה</a>
        {/* Every stage as text, for screen readers and search engines. */}
        <ul className="sr-only">
          {stages.slice(1).map((s) => (
            <li key={s.title}>
              {s.title}: {s.text}
            </li>
          ))}
        </ul>

        {/* Phones (2026-10-14): a compact card that clears the floating buttons
            (accessibility + chat sit ~130px up the right edge), so the model
            gets most of the screen; desktop keeps the side panel. */}
        <div className="absolute inset-x-0 bottom-0 md:inset-y-0 md:right-0 md:left-auto md:w-[420px] flex flex-col justify-end md:justify-center px-4 pb-[calc(136px+env(safe-area-inset-bottom,0px))] md:p-12">
          <div ref={panelRef} className="relative rounded-2xl border border-white/10 bg-[#0B1224]/80 backdrop-blur-md p-4 md:p-7 md:bg-transparent md:border-0 md:backdrop-blur-0">
            <span aria-hidden="true" className="absolute inset-x-4 top-2 h-[3px] overflow-hidden rounded-full bg-white/10 md:hidden">
              <span className="block h-full bg-[#8FB3FF] transition-[width] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]" style={{ width: `${((stage + 1) / stages.length) * 100}%` }} />
            </span>
            <p dir="ltr" className="mb-1.5 mt-1 md:mt-0 md:mb-2 text-right text-[13px] font-medium text-[#8FB3FF]" aria-hidden="true">
              {String(stage + 1).padStart(2, '0')} / {String(stages.length).padStart(2, '0')}
            </p>
            <div key={stage} className="animate-[exploded-caption_260ms_cubic-bezier(0.23,1,0.32,1)]">
              {stage === 0 ? (
                <h2 id={titleId} className="cin-display !text-[clamp(1.6rem,3.4vw,2.75rem)] mb-2 md:mb-3 text-white">{current.title}</h2>
              ) : (
                <h3 className="text-[1.3rem] md:text-[clamp(1.4rem,2.6vw,2rem)] font-bold mb-1.5 md:mb-3 text-white">{current.title}</h3>
              )}
              <p className="text-white/75 text-[15px] leading-snug line-clamp-3 md:line-clamp-none md:text-lg md:leading-relaxed">{current.text}</p>
              {current.photo && (
                <figure className="mt-3 md:mt-4 flex items-center gap-3 md:block">
                  <img
                    src={`/generated-images/parts/${current.photo}-720.webp`}
                    srcSet={`/generated-images/parts/${current.photo}-360.webp 360w, /generated-images/parts/${current.photo}-720.webp 720w`}
                    sizes="(min-width: 768px) 320px, 96px"
                    alt={current.photoAlt || ''}
                    width={720}
                    height={540}
                    decoding="async"
                    className="h-[48px] w-[64px] shrink-0 rounded-lg object-cover ring-1 ring-white/10 md:h-auto md:w-full md:max-w-[320px] md:rounded-xl"
                  />
                  <figcaption className="text-xs text-white/55 md:mt-2">ככה זה נראה במציאות</figcaption>
                </figure>
              )}
              {current.link && (
                // Static pages (/tools/…/) live outside the app — a plain link.
                current.link.to.endsWith('/') ? (
                  <a href={current.link.to} className="mt-2 md:mt-4 inline-flex text-[#8FB3FF] font-semibold hover:underline">
                    {current.link.label} ←
                  </a>
                ) : (
                <Link to={current.link.to} className="mt-2 md:mt-4 inline-flex text-[#8FB3FF] font-semibold hover:underline">
                  {current.link.label} ←
                </Link>
                )
              )}
              {stage === stages.length - 1 && props.finalCta}
            </div>
          </div>
          <div className="mt-3 hidden md:block">{props.credit}</div>
        </div>

        {/* Progress rail */}
        <div aria-hidden="true" className="absolute left-5 top-1/2 -translate-y-1/2 hidden md:flex flex-col gap-2">
          {stages.map((s, i) => (
            <span key={s.title} className={`block w-1.5 rounded-full transition-[height,background-color] duration-300 ${i === stage ? 'h-6 bg-[#8FB3FF]' : 'h-1.5 bg-white/25'}`} />
          ))}
        </div>
      </div>
      <span id={`after-${props.id}`} />
    </section>
  );
}

/** CC BY credit line for a Sketchfab model. */
export function ModelCredit({ author, url }: { author: string; url: string }) {
  return (
    <p className="text-[11px] text-white/60">
      מודל תלת־ממד: {author} ·{' '}
      <a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-white/20 hover:text-white/60">
        Sketchfab
      </a>{' '}
      ·{' '}
      <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" className="underline decoration-white/20 hover:text-white/60">
        CC BY 4.0
      </a>{' '}
      · הותאם לאתר
    </p>
  );
}
